import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useCreditsQuery } from '@/hooks/useAgentWorkspace';

/**
 * State for the legacy DashboardLayout sidebar.
 *
 * Since the aikka OS workspace took over `/agent` it owns the conversation
 * history, spaces and agent switcher in its own sidebar, so this one no longer
 * carries them: DashboardLayout is now a thin shell for billing, settings and
 * the product pages. What remains is navigation, the user menu and the credit
 * balance (still useful next to billing).
 */
export function useSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [productsOpen, setProductsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const { data: credits } = useCreditsQuery();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userMenuOpen]);

  const initials = user?.name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  const isActive = (path: string) => location.pathname === path;
  const isActivePrefix = (prefix: string) => location.pathname.startsWith(prefix);

  return {
    location,
    navigate,
    user,
    logout,
    productsOpen,
    setProductsOpen,
    userMenuOpen,
    setUserMenuOpen,
    userMenuRef,
    credits: credits ?? null,
    initials,
    isActive,
    isActivePrefix,
  };
}
